/**
 * Range-rings overlay settings — concentric distance rings centred on own ship,
 * with an interval, a count, an on/off toggle and optional cardinal spokes. Kept
 * on the device (localStorage) like the satellite overlay so the user's setup
 * survives reloads.
 */
import { create } from 'zustand';

const ON_LS = 'acp.rangeRings.on.v1';
const INT_LS = 'acp.rangeRings.interval.v1';
const CNT_LS = 'acp.rangeRings.count.v1';
const SPK_LS = 'acp.rangeRings.spokes.v1';
const AUTO_LS = 'acp.rangeRings.auto.v1';

function num(key: string, def: number): number {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const n = v == null ? def : parseFloat(v);
    return Number.isFinite(n) ? n : def;
  } catch {
    return def;
  }
}
function bool(key: string, def: boolean): boolean {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    return v == null ? def : v === '1';
  } catch {
    return def;
  }
}
function save(key: string, v: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

/** Sensible bounds so the rings stay useful and never runaway. */
const MIN_INTERVAL = 0.1;
const MAX_INTERVAL = 50;
const MIN_COUNT = 1;
const MAX_COUNT = 12;

/**
 * The round intervals Auto snaps to, nautical miles. Auto picks the largest of
 * these that keeps the rings a useful distance apart at the current zoom, so as
 * we zoom out the spacing steps up 0.1 → 0.25 → 0.5 → 1 → 2 → 5 → 10 and on,
 * always landing on a number that's quick to read off the chart rather than an
 * awkward 0.37 nm.
 */
export const RING_LADDER = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100, 200];

/** The largest ladder interval at or below the target, never below the smallest. */
export function snapRingInterval(targetNm: number): number {
  for (let i = RING_LADDER.length - 1; i >= 0; i--) {
    if (RING_LADDER[i] <= targetNm) return RING_LADDER[i];
  }
  return RING_LADDER[0];
}

/**
 * Pick an auto ring interval from the visible span. We aim for roughly six rings
 * across the shorter side of the view (spanNm is that dimension in nautical
 * miles), then snap to a round ladder value. So the rings stay legible and evenly
 * spread whether we're zoomed into a marina or out to a whole passage.
 */
export function autoRingIntervalNm(spanNm: number): number {
  if (!Number.isFinite(spanNm) || spanNm <= 0) return RING_LADDER[0];
  return snapRingInterval(spanNm / 6);
}

interface RangeRingsState {
  /** Whether the range-rings overlay is drawn. */
  enabled: boolean;
  setEnabled: (b: boolean) => void;
  /** Distance between rings, nautical miles. */
  intervalNm: number;
  setIntervalNm: (n: number) => void;
  /** How many rings to draw out from the boat. */
  count: number;
  setCount: (n: number) => void;
  /** Draw N/E/S/W bearing spokes from the boat. */
  spokes: boolean;
  setSpokes: (b: boolean) => void;
  /** Auto spacing: pick a round interval from the current zoom instead of the
   *  fixed intervalNm. The manual interval stays as the fallback when off. */
  auto: boolean;
  setAuto: (b: boolean) => void;
}

export const useRangeRingsStore = create<RangeRingsState>((set) => ({
  enabled: bool(ON_LS, false),
  setEnabled: (enabled) => {
    save(ON_LS, enabled ? '1' : '0');
    set({ enabled });
  },
  intervalNm: Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, num(INT_LS, 0.5))),
  setIntervalNm: (n) => {
    const v = Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, n));
    save(INT_LS, String(v));
    set({ intervalNm: v });
  },
  count: Math.round(Math.min(MAX_COUNT, Math.max(MIN_COUNT, num(CNT_LS, 4)))),
  setCount: (n) => {
    const v = Math.round(Math.min(MAX_COUNT, Math.max(MIN_COUNT, n)));
    save(CNT_LS, String(v));
    set({ count: v });
  },
  spokes: bool(SPK_LS, false),
  setSpokes: (spokes) => {
    save(SPK_LS, spokes ? '1' : '0');
    set({ spokes });
  },
  auto: bool(AUTO_LS, false),
  setAuto: (auto) => {
    save(AUTO_LS, auto ? '1' : '0');
    set({ auto });
  },
}));
